import { LogoFidexaPrincipal } from "@/assets";
import { Text } from "@/design-system";
import { useScrollDepth } from "@/hooks";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "../content/company";
import type { LegalDocument } from "../content/types";
import { useActiveSection } from "../hooks/useActiveSection";
import { formatLegalDate } from "../utils";
import { LegalHero } from "./legalHero";
import { LegalHighlights } from "./legalHighlights";
import { LegalSectionView } from "./legalSectionView";
import { LegalToc } from "./legalToc";

type IncidentLayoutProps = {
  document: LegalDocument;
};

/**
 * A casca do relatório de incidente: a mesma leitura dos documentos jurídicos
 * (essencial → índice e texto), mas fechada. Sem header, sem footer e sem
 * nenhum link para o resto do site — é enviada por link a quem foi afetado,
 * não é porta de entrada. Por isso também fica em `noindex`.
 */
export const IncidentLayout = ({ document }: IncidentLayoutProps) => {
  useScrollDepth();
  const sectionIds = useMemo(
    () => document.sections.map(section => section.id),
    [document.sections]
  );
  const activeId = useActiveSection(sectionIds);

  const url = `${SITE_URL}${document.path}`;
  const ogTitle = `${document.seoTitle} | Fidexa`;

  return (
    <>
      <Helmet>
        <title>{document.seoTitle}</title>
        <meta name="description" content={document.description} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={url} />

        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={document.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={document.description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      </Helmet>

      <div className="bg-ds-paper min-h-screen">
        <main className="flex flex-col gap-16 pb-16 md:gap-20">
          <div className="mx-auto w-full max-w-6xl px-4 pt-8 sm:px-6 md:pt-12 lg:px-8">
            <span role="img" aria-label="Fidexa" className="inline-flex">
              <LogoFidexaPrincipal width={123} height={30} />
            </span>
          </div>

          <LegalHero document={document} className="pt-0 md:pt-0" />
          <LegalHighlights highlights={document.highlights} />

          <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8">
            <div className="lg:col-span-4 xl:col-span-3">
              <LegalToc
                sections={document.sections}
                activeId={activeId}
                stickyClassName="top-8"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-10 lg:col-span-8 xl:col-span-8">
              {document.sections.map((section, index) => (
                <LegalSectionView
                  key={section.id}
                  section={section}
                  number={index + 1}
                />
              ))}
              <Text size="small" tone="muted">
                Equipe Fidexa ·{" "}
                <time dateTime={document.updatedAt}>
                  {formatLegalDate(document.updatedAt)}
                </time>
              </Text>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};
